import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageContainer } from '../components/AdminContentWrapper';
import CatalogTree from '../../../components/iceberg/CatalogTree';
import CatalogPropertiesPanel from '../../../components/iceberg/CatalogPropertiesPanel';
import UserProfileService from '../../../services/UserProfileService';
import IcebergCatalogService from '../../../services/IcebergCatalogService';
import FileUploadWizard from '../../../components/iceberg/FileUploadWizard';
import ConfirmDestructiveDialog from '../../../components/common/ConfirmDestructiveDialog';

const resolveAccountId = (profile) => profile?.account_id || profile?.accountUuid || profile?.account_uuid;

const asList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

// fetchWithAuth lanza "HTTP <status>: <cuerpo>"; si el cuerpo es el JSON
// {"message": ...} del backend, se muestra solo ese mensaje.
export const readableError = (error, fallback) => {
  const raw = error?.message || '';
  const match = raw.match(/^HTTP \d+: (.*)$/s);
  if (match) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed?.message) return parsed.message;
    } catch {
      // cuerpo no JSON: se usa tal cual
    }
  }
  return raw || fallback;
};

const MAX_SCHEMAS_PER_DATABASE = 100;

// Paneles del árbol y de propiedades: mismo estilo que Section
// (AdminContentWrapper.jsx).
const panelSx = {
  backgroundColor: 'var(--wb-bg-panel)',
  borderRadius: 2,
  border: '1px solid var(--wb-border)',
  boxShadow: '0 12px 32px var(--wb-x-shadow-0f172a59, rgba(15, 23, 42, 0.35))'
};

const panelTitleSx = { color: 'var(--wb-text)', fontWeight: 600, fontSize: '0.95rem' };

/**
 * Explorador del catálogo Iceberg (Fase 1 del plan "Catálogo Iceberg S3"):
 * Database -> Schema (máx. 100/database, aplicado por el backend) -> Table,
 * en forma de árbol (CatalogTree) con las propiedades y acciones del
 * elemento seleccionado a la derecha (CatalogPropertiesPanel).
 * Table no se crea/edita a mano acá -- sale de la ingesta de archivos (ver
 * docstring de iceberg_table/app.py) -- pero SÍ se puede disparar esa
 * ingesta desde acá, con schema destino explícito (ver FileUploadWizard y
 * "Subir Excel / CSV" en las propiedades del Schema), en vez de solo desde el flujo
 * genérico de "conexión de Excel" (SemanticConnections.jsx), que siempre
 * cae en el schema "Default" auto-provisionado del tenant.
 */
const IcebergCatalogContent = () => {
  const [accountId, setAccountId] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado por-database: schemas cargados, expandidos en el árbol, cargando.
  const [schemasByDatabase, setSchemasByDatabase] = useState({});
  const [expandedDatabases, setExpandedDatabases] = useState({});
  const [loadingSchemasFor, setLoadingSchemasFor] = useState(null);

  // Estado por-schema: tablas cargadas, expandidos en el árbol, cargando.
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const [loadingTablesFor, setLoadingTablesFor] = useState(null);

  // Elemento seleccionado en el árbol (sus propiedades se ven a la derecha):
  // { type: 'database', databaseId } | { type: 'schema', databaseId, schemaId }
  // | { type: 'table', databaseId, schemaId, tableName }.
  const [selected, setSelected] = useState(null);

  const [createDbOpen, setCreateDbOpen] = useState(false);
  const [createDbName, setCreateDbName] = useState('');
  const [createDbDescription, setCreateDbDescription] = useState('');
  const [isSavingDb, setIsSavingDb] = useState(false);

  const [createSchemaFor, setCreateSchemaFor] = useState(null); // iceberg_database_id o null
  const [createSchemaName, setCreateSchemaName] = useState('');
  const [createSchemaDescription, setCreateSchemaDescription] = useState('');
  const [isSavingSchema, setIsSavingSchema] = useState(false);

  // Fase 5.1 -- historial de snapshots ("time travel") de una tabla.
  const [snapshotsFor, setSnapshotsFor] = useState(null); // { glueTableName } o null
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsMessage, setSnapshotsMessage] = useState('');
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);

  // Fase 5.2 -- revisión de un cambio de esquema destructivo pendiente
  // (columna eliminada / tipo que se angosta) antes de aprobarlo/rechazarlo.
  const [schemaReviewFor, setSchemaReviewFor] = useState(null); // { schemaId, table, diff } o null
  const [isLoadingSchemaReview, setIsLoadingSchemaReview] = useState(false);
  const [isResolvingSchemaReview, setIsResolvingSchemaReview] = useState(false);

  // Fase 5.3 -- "Full Clone" de una tabla: copia COMPLETA de los datos
  // (nunca zero-copy). cloneFor: { schemaId, glueDatabaseName, table } o null.
  const [cloneFor, setCloneFor] = useState(null);
  const [cloneTargetSchemaId, setCloneTargetSchemaId] = useState('');
  const [cloneTargetName, setCloneTargetName] = useState('');
  const [cloneSnapshotId, setCloneSnapshotId] = useState('');
  const [cloneTargetSchemas, setCloneTargetSchemas] = useState([]);
  const [cloneSourceSnapshots, setCloneSourceSnapshots] = useState([]);
  const [isLoadingCloneOptions, setIsLoadingCloneOptions] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  // Wizard de carga de Excel/CSV con schema destino explícito -- ver
  // FileUploadWizard. uploadWizardFor: { schemaId, glueDatabaseName } o null.
  const [uploadWizardFor, setUploadWizardFor] = useState(null);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Borrado pendiente de confirmar: { kind: 'database', database } o
  // { kind: 'schema', databaseId, schema }.
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadDatabases = useCallback(async (resolvedAccountId) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await IcebergCatalogService.listDatabases(resolvedAccountId);
      setDatabases(asList(response));
    } catch (loadError) {
      console.error('Error cargando databases del catálogo Iceberg:', loadError);
      setError(loadError.message || 'No se pudieron cargar las databases del catálogo.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const profile = await UserProfileService.getProfile();
        const resolvedAccountId = resolveAccountId(profile);
        if (!resolvedAccountId) {
          setError('No pudimos determinar la cuenta asociada.');
          return;
        }
        setAccountId(resolvedAccountId);
        await loadDatabases(resolvedAccountId);
      } catch (profileError) {
        console.error('Error cargando el perfil del usuario:', profileError);
        setError(profileError.message || 'No se pudo cargar el perfil del usuario.');
      }
    })();
  }, [loadDatabases]);

  const handleToggleDatabase = async (database) => {
    const databaseId = database.iceberg_database_id;
    const nextExpanded = !expandedDatabases[databaseId];
    setExpandedDatabases((prev) => ({ ...prev, [databaseId]: nextExpanded }));
    if (nextExpanded && !schemasByDatabase[databaseId]) {
      try {
        setLoadingSchemasFor(databaseId);
        const response = await IcebergCatalogService.listSchemas(accountId, { icebergDatabaseId: databaseId });
        setSchemasByDatabase((prev) => ({ ...prev, [databaseId]: asList(response) }));
      } catch (schemasError) {
        console.error('Error cargando schemas:', schemasError);
        notify(schemasError.message || 'No se pudieron cargar los schemas.', 'error');
      } finally {
        setLoadingSchemasFor(null);
      }
    }
  };

  const handleToggleSchema = async (schema) => {
    const schemaId = schema.iceberg_schema_id;
    const nextExpanded = !expandedSchemas[schemaId];
    setExpandedSchemas((prev) => ({ ...prev, [schemaId]: nextExpanded }));
    if (nextExpanded && !tablesBySchema[schemaId]) {
      try {
        setLoadingTablesFor(schemaId);
        const response = await IcebergCatalogService.listTables(schemaId, { accountId });
        setTablesBySchema((prev) => ({ ...prev, [schemaId]: asList(response) }));
      } catch (tablesError) {
        console.error('Error cargando tablas físicas:', tablesError);
        notify(tablesError.message || 'No se pudieron cargar las tablas.', 'error');
      } finally {
        setLoadingTablesFor(null);
      }
    }
  };

  // Clic en una fila del árbol: la selecciona y, si estaba colapsada, la expande.
  const handleSelect = (selection, expand) => {
    setSelected(selection);
    if (expand) expand();
  };

  // Resuelve la selección contra los datos ya cargados, para que el panel
  // muestre siempre la versión más reciente (p. ej. tras recargar).
  const resolveSelection = () => {
    if (!selected) return null;
    const database = databases.find((db) => db.iceberg_database_id === selected.databaseId);
    if (!database) return null;
    if (selected.type === 'database') return { type: 'database', database };
    const schema = (schemasByDatabase[selected.databaseId] || []).find((sc) => sc.iceberg_schema_id === selected.schemaId);
    if (!schema) return null;
    if (selected.type === 'schema') return { type: 'schema', database, schema };
    const table = (tablesBySchema[selected.schemaId] || []).find((tb) => tb.glue_table_name === selected.tableName);
    return table ? { type: 'table', database, schema, table } : null;
  };

  const handleCreateDatabase = async () => {
    if (!createDbName.trim()) {
      notify('El nombre de la database es obligatorio.', 'error');
      return;
    }
    setIsSavingDb(true);
    try {
      await IcebergCatalogService.createDatabase({ name: createDbName.trim(), description: createDbDescription.trim() });
      setCreateDbOpen(false);
      setCreateDbName('');
      setCreateDbDescription('');
      notify('Database creada correctamente.');
      await loadDatabases(accountId);
    } catch (createError) {
      console.error('Error creando database:', createError);
      notify(createError.message || 'No se pudo crear la database.', 'error');
    } finally {
      setIsSavingDb(false);
    }
  };

  const handleDeleteDatabase = async (database) => {
    try {
      await IcebergCatalogService.softDeleteDatabase(database.iceberg_database_id, { accountId });
      setPendingDelete(null);
      setSelected((prev) => (prev?.databaseId === database.iceberg_database_id ? null : prev));
      notify('Database eliminada.');
      await loadDatabases(accountId);
    } catch (deleteError) {
      console.error('Error eliminando database:', deleteError);
      notify(readableError(deleteError, 'No se pudo eliminar la database.'), 'error');
    }
  };

  const handleCreateSchema = async () => {
    if (!createSchemaName.trim() || !createSchemaFor) {
      notify('El nombre del schema es obligatorio.', 'error');
      return;
    }
    setIsSavingSchema(true);
    try {
      await IcebergCatalogService.createSchema({
        icebergDatabaseId: createSchemaFor,
        name: createSchemaName.trim(),
        description: createSchemaDescription.trim()
      });
      notify('Schema creado correctamente -- se registró también su Glue database física.');
      const response = await IcebergCatalogService.listSchemas(accountId, { icebergDatabaseId: createSchemaFor });
      setSchemasByDatabase((prev) => ({ ...prev, [createSchemaFor]: asList(response) }));
      setExpandedDatabases((prev) => ({ ...prev, [createSchemaFor]: true }));
      await loadDatabases(accountId); // contador de schemas
      setCreateSchemaFor(null);
      setCreateSchemaName('');
      setCreateSchemaDescription('');
    } catch (createError) {
      console.error('Error creando schema:', createError);
      notify(createError.message || 'No se pudo crear el schema.', 'error');
    } finally {
      setIsSavingSchema(false);
    }
  };

  const handleDeleteSchema = async (databaseId, schema) => {
    try {
      await IcebergCatalogService.softDeleteSchema(schema.iceberg_schema_id, { accountId });
      setPendingDelete(null);
      setSelected((prev) => (prev?.schemaId === schema.iceberg_schema_id ? null : prev));
      notify('Schema eliminado.');
      const response = await IcebergCatalogService.listSchemas(accountId, { icebergDatabaseId: databaseId });
      setSchemasByDatabase((prev) => ({ ...prev, [databaseId]: asList(response) }));
      // El contador de schemas de la database cambió.
      await loadDatabases(accountId);
    } catch (deleteError) {
      console.error('Error eliminando schema:', deleteError);
      notify(readableError(deleteError, 'No se pudo eliminar el schema.'), 'error');
    }
  };

  const renderDeleteDialog = () => {
    if (!pendingDelete) return null;
    if (pendingDelete.kind === 'database') {
      const { database } = pendingDelete;
      const schemaCount = database.schema_count ?? 0;
      return (
        <ConfirmDestructiveDialog
          open
          title="Eliminar database"
          onClose={() => setPendingDelete(null)}
          onConfirm={() => handleDeleteDatabase(database)}
          progressMessage="Eliminando la database…"
          blockedReason={schemaCount > 0
            ? `Esta database tiene ${schemaCount} schema(s). Elimínalos primero (selecciona cada schema en el árbol y usa "Eliminar schema") y luego vuelve a intentarlo.`
            : null}
        >
          <Typography variant="body1">
            ¿Eliminar la database <strong>{database.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Dejará de aparecer en el Catálogo Iceberg.
          </Typography>
        </ConfirmDestructiveDialog>
      );
    }
    const { databaseId, schema } = pendingDelete;
    return (
      <ConfirmDestructiveDialog
        open
        title="Eliminar schema"
        onClose={() => setPendingDelete(null)}
        onConfirm={() => handleDeleteSchema(databaseId, schema)}
        progressMessage="Eliminando el schema…"
      >
        <Typography variant="body1">
          ¿Eliminar el schema <strong>{schema.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Sus tablas dejarán de aparecer en el Catálogo y el asistente dejará de consultarlas.
          Los archivos de datos almacenados no se borran.
        </Typography>
      </ConfirmDestructiveDialog>
    );
  };

  const handleViewSnapshots = async (schema, table) => {
    setSnapshotsFor({ glueTableName: table.glue_table_name });
    setSnapshots([]);
    setSnapshotsMessage('');
    setIsLoadingSnapshots(true);
    try {
      const response = await IcebergCatalogService.getSnapshots(schema.glue_database_name, table.glue_table_name);
      setSnapshots(response?.snapshots || []);
      setSnapshotsMessage(response?.message || '');
    } catch (snapshotsError) {
      console.error('Error cargando el historial de snapshots:', snapshotsError);
      notify(snapshotsError.message || 'No se pudo cargar el historial de esta tabla.', 'error');
      setSnapshotsFor(null);
    } finally {
      setIsLoadingSnapshots(false);
    }
  };

  // Fase 5.2 -- re-consulta las tablas de un schema (no usa el cache de
  // tablesBySchema) para que, tras aprobar/rechazar, has_pending_schema_review
  // se refresque en la lista.
  const refreshTablesForSchema = async (schemaId) => {
    try {
      const response = await IcebergCatalogService.listTables(schemaId, { accountId });
      setTablesBySchema((prev) => ({ ...prev, [schemaId]: asList(response) }));
    } catch (refreshError) {
      console.error('Error refrescando las tablas del schema:', refreshError);
    }
  };

  const handleOpenSchemaReview = async (schema, table) => {
    setSchemaReviewFor({ schemaId: schema.iceberg_schema_id, table, diff: null });
    setIsLoadingSchemaReview(true);
    try {
      const row = await IcebergCatalogService.getTable(accountId, table.iceberg_table_id);
      setSchemaReviewFor({ schemaId: schema.iceberg_schema_id, table, diff: row?.pending_schema_review?.diff || null });
    } catch (loadError) {
      console.error('Error cargando el detalle del cambio de esquema:', loadError);
      notify(loadError.message || 'No se pudo cargar el detalle del cambio de esquema.', 'error');
      setSchemaReviewFor(null);
    } finally {
      setIsLoadingSchemaReview(false);
    }
  };

  const handleResolveSchemaReview = async (decision) => {
    if (!schemaReviewFor) {
      return;
    }
    const { schemaId, table } = schemaReviewFor;
    setIsResolvingSchemaReview(true);
    try {
      await IcebergCatalogService.resolveSchemaReview(accountId, table.iceberg_table_id, decision);
      notify(
        decision === 'approve'
          ? 'Cambio de esquema aprobado -- la tabla se está actualizando.'
          : 'Cambio de esquema rechazado -- la tabla no se modificó.'
      );
      setSchemaReviewFor(null);
      await refreshTablesForSchema(schemaId);
    } catch (resolveError) {
      console.error('Error resolviendo la revisión de esquema:', resolveError);
      notify(resolveError.message || 'No se pudo resolver la revisión de esquema.', 'error');
    } finally {
      setIsResolvingSchemaReview(false);
    }
  };

  // Fase 5.3 -- abre el diálogo de "Full Clone" precargando los schemas
  // del tenant (destino posible) y el historial de snapshots de la tabla
  // origen (para clonar "como estaba" en un momento puntual, opcional).
  const handleOpenClone = async (schema, table) => {
    setCloneFor({ schemaId: schema.iceberg_schema_id, glueDatabaseName: schema.glue_database_name, table });
    setCloneTargetSchemaId(schema.iceberg_schema_id);
    setCloneTargetName(`${table.glue_table_name}_copia`);
    setCloneSnapshotId('');
    setCloneTargetSchemas([]);
    setCloneSourceSnapshots([]);
    setIsLoadingCloneOptions(true);
    try {
      const [schemasResponse, snapshotsResponse] = await Promise.all([
        IcebergCatalogService.listSchemas(accountId),
        IcebergCatalogService.getSnapshots(schema.glue_database_name, table.glue_table_name).catch(() => null)
      ]);
      setCloneTargetSchemas(asList(schemasResponse));
      setCloneSourceSnapshots(snapshotsResponse?.snapshots || []);
    } catch (loadError) {
      console.error('Error cargando opciones de clonado:', loadError);
      notify('No se pudieron cargar las opciones de clonado.', 'error');
    } finally {
      setIsLoadingCloneOptions(false);
    }
  };

  const handleConfirmClone = async () => {
    if (!cloneFor) {
      return;
    }
    if (!cloneTargetName.trim()) {
      notify('El nombre destino es obligatorio.', 'error');
      return;
    }
    setIsCloning(true);
    try {
      await IcebergCatalogService.cloneTable(accountId, cloneFor.table.iceberg_table_id, {
        targetIcebergSchemaId: cloneTargetSchemaId,
        targetTableName: cloneTargetName.trim(),
        snapshotId: cloneSnapshotId || undefined
      });
      notify('Clonado iniciado -- la copia completa de los datos puede tardar unos minutos.');
      const targetSchemaId = cloneTargetSchemaId;
      setCloneFor(null);
      await refreshTablesForSchema(targetSchemaId);
    } catch (cloneError) {
      console.error('Error iniciando el clonado:', cloneError);
      notify(cloneError.message || 'No se pudo iniciar el clonado.', 'error');
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <PageContainer
      title="Catálogo Iceberg"
      description="Administra la jerarquía física Database → Schema → Table respaldada por tablas Apache Iceberg en S3, catalogadas en AWS Glue. El modelo semántico de negocio se sigue administrando desde Modelo semántico."
      actions={[
        <Button key="create-db" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDbOpen(true)}>
          Nueva database
        </Button>
      ]}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : databases.length === 0 ? (
        <Alert severity="info">Todavía no hay databases en el catálogo Iceberg de esta cuenta.</Alert>
      ) : (
        // Árbol a la izquierda y propiedades a la derecha a CUALQUIER ancho
        // (nunca apilados): en pantallas angostas el árbol se reduce y cada
        // panel tiene su propio scroll.
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(160px, 40%) minmax(0, 1fr)',
              md: 'minmax(260px, 380px) minmax(0, 1fr)'
            },
            gap: { xs: 1.5, md: 3 },
            alignItems: 'start'
          }}
        >
          <Box sx={{ ...panelSx, p: { xs: 1, md: 1.5 }, position: 'sticky', top: 16, minWidth: 0 }}>
            <Typography sx={{ ...panelTitleSx, px: 1, pt: 0.5, pb: 1 }}>Explorador</Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
              <CatalogTree
                databases={databases}
                schemasByDatabase={schemasByDatabase}
                tablesBySchema={tablesBySchema}
                expandedDatabases={expandedDatabases}
                expandedSchemas={expandedSchemas}
                loadingSchemasFor={loadingSchemasFor}
                loadingTablesFor={loadingTablesFor}
                selected={selected}
                onToggleDatabase={handleToggleDatabase}
                onToggleSchema={handleToggleSchema}
                onSelect={handleSelect}
              />
            </Box>
          </Box>
          <Box sx={{ ...panelSx, p: { xs: 1.5, md: 3 }, minWidth: 0, overflowX: 'auto' }}>
            <Typography sx={{ ...panelTitleSx, mb: 2 }}>Propiedades</Typography>
            <CatalogPropertiesPanel
              selection={resolveSelection()}
              maxSchemasPerDatabase={MAX_SCHEMAS_PER_DATABASE}
              onCreateSchema={(database) => setCreateSchemaFor(database.iceberg_database_id)}
              onDeleteDatabase={(database) => setPendingDelete({ kind: 'database', database })}
              onUploadFile={(schema) => setUploadWizardFor({
                schemaId: schema.iceberg_schema_id,
                glueDatabaseName: schema.glue_database_name
              })}
              onDeleteSchema={(schema) => setPendingDelete({
                kind: 'schema', databaseId: schema.iceberg_database_id, schema
              })}
              onViewSnapshots={handleViewSnapshots}
              onClone={handleOpenClone}
              onReviewSchema={handleOpenSchemaReview}
            />
          </Box>
        </Box>
      )}

      {/* Crear database */}
      <Dialog open={createDbOpen} onClose={() => !isSavingDb && setCreateDbOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva database</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            value={createDbName}
            onChange={(event) => setCreateDbName(event.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Descripción (opcional)"
            value={createDbDescription}
            onChange={(event) => setCreateDbDescription(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDbOpen(false)} disabled={isSavingDb}>Cancelar</Button>
          <Button onClick={handleCreateDatabase} variant="contained" disabled={isSavingDb}>
            {isSavingDb ? 'Creando…' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crear schema */}
      <Dialog open={Boolean(createSchemaFor)} onClose={() => !isSavingSchema && setCreateSchemaFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo schema</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se creará una Glue database física real para este schema -- no requiere un bucket todavía.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            value={createSchemaName}
            onChange={(event) => setCreateSchemaName(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Descripción (opcional)"
            value={createSchemaDescription}
            onChange={(event) => setCreateSchemaDescription(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSchemaFor(null)} disabled={isSavingSchema}>Cancelar</Button>
          <Button onClick={handleCreateSchema} variant="contained" disabled={isSavingSchema}>
            {isSavingSchema ? 'Creando…' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historial de snapshots (Fase 5.1, "time travel" en UI) */}
      <Dialog open={Boolean(snapshotsFor)} onClose={() => setSnapshotsFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Historial de versiones{snapshotsFor ? ` -- ${snapshotsFor.glueTableName}` : ''}</DialogTitle>
        <DialogContent>
          {isLoadingSnapshots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : snapshotsMessage ? (
            <Alert severity="info">{snapshotsMessage}</Alert>
          ) : snapshots.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Esta tabla todavía no tiene snapshots registrados.
            </Typography>
          ) : (
            <List dense>
              {snapshots.map((snapshot) => (
                <ListItem key={snapshot.snapshot_id} divider>
                  <ListItemText
                    primary={`${snapshot.operation || 'operación'} · ${snapshot.committed_at || ''}`}
                    secondary={`snapshot_id: ${snapshot.snapshot_id}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotsFor(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Revisión de cambio de esquema destructivo (Fase 5.2, schema evolution asistido) */}
      <Dialog open={Boolean(schemaReviewFor)} onClose={() => !isResolvingSchemaReview && setSchemaReviewFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cambio de esquema pendiente{schemaReviewFor ? ` -- ${schemaReviewFor.table.glue_table_name}` : ''}</DialogTitle>
        <DialogContent>
          {isLoadingSchemaReview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                La última subida de archivo para esta tabla elimina columnas o cambia el tipo de datos de
                alguna columna existente. Revisa el detalle antes de decidir -- si apruebas, la tabla se
                actualiza con el esquema nuevo; si rechazas, la tabla queda igual que está hoy.
              </Alert>
              {schemaReviewFor?.diff?.removed?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Columnas eliminadas</Typography>
                  <List dense>
                    {schemaReviewFor.diff.removed.map((col) => (
                      <ListItem key={col}>
                        <ListItemText primary={col} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {schemaReviewFor?.diff?.type_changed?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Columnas con tipo de dato distinto</Typography>
                  <List dense>
                    {schemaReviewFor.diff.type_changed.map((change) => (
                      <ListItem key={change.column}>
                        <ListItemText primary={change.column} secondary={`${change.old_type} → ${change.new_type}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {schemaReviewFor?.diff?.added?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Columnas nuevas (se agregan sin problema)</Typography>
                  <List dense>
                    {schemaReviewFor.diff.added.map((col) => (
                      <ListItem key={col}>
                        <ListItemText primary={col} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaReviewFor(null)} disabled={isResolvingSchemaReview}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => handleResolveSchemaReview('reject')}
            disabled={isResolvingSchemaReview || isLoadingSchemaReview}
          >
            Rechazar
          </Button>
          <Button
            variant="contained"
            onClick={() => handleResolveSchemaReview('approve')}
            disabled={isResolvingSchemaReview || isLoadingSchemaReview}
          >
            {isResolvingSchemaReview ? 'Aplicando…' : 'Aprobar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clonar tabla -- Full Clone (Fase 5.3) */}
      <Dialog open={Boolean(cloneFor)} onClose={() => !isCloning && setCloneFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Clonar tabla{cloneFor ? ` -- ${cloneFor.table.glue_table_name}` : ''}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esto crea una copia <strong>COMPLETA</strong> de los datos de esta tabla (<strong>Full Clone</strong>).
            No es un "zero-copy clone" -- la copia ocupa espacio de almacenamiento adicional, igual que si
            volvieras a subir el archivo.
          </Alert>
          {isLoadingCloneOptions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TextField
                select
                fullWidth
                label="Schema destino"
                value={cloneTargetSchemaId}
                onChange={(event) => setCloneTargetSchemaId(event.target.value)}
                sx={{ mb: 2 }}
              >
                {cloneTargetSchemas.map((targetSchema) => (
                  <MenuItem key={targetSchema.iceberg_schema_id} value={targetSchema.iceberg_schema_id}>
                    {targetSchema.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Nombre de la tabla destino"
                value={cloneTargetName}
                onChange={(event) => setCloneTargetName(event.target.value)}
                helperText="Solo letras, números y guion bajo."
                sx={{ mb: 2 }}
              />
              <TextField
                select
                fullWidth
                label="Clonar como estaba en"
                value={cloneSnapshotId}
                onChange={(event) => setCloneSnapshotId(event.target.value)}
                helperText="Opcional -- por defecto clona el estado actual de la tabla."
              >
                <MenuItem value="">Estado actual</MenuItem>
                {cloneSourceSnapshots.map((snapshot) => (
                  <MenuItem key={snapshot.snapshot_id} value={snapshot.snapshot_id}>
                    {`${snapshot.committed_at || ''} (${snapshot.operation || 'operación'})`}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneFor(null)} disabled={isCloning}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmClone}
            disabled={isCloning || isLoadingCloneOptions}
          >
            {isCloning ? 'Clonando…' : 'Clonar (copia completa)'}
          </Button>
        </DialogActions>
      </Dialog>

      {uploadWizardFor && (
        <FileUploadWizard
          open={Boolean(uploadWizardFor)}
          onClose={() => setUploadWizardFor(null)}
          accountId={accountId}
          icebergSchemaId={uploadWizardFor.schemaId}
          glueDatabaseName={uploadWizardFor.glueDatabaseName}
          onComplete={() => refreshTablesForSchema(uploadWizardFor.schemaId)}
        />
      )}

      {renderDeleteDialog()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

// Sin gate de UI a propósito -- a diferencia de las pantallas root-only
// (RequireBillingRole/RequireRootRole, gateadas por cognito:groups), este
// módulo es de permiso GRANULAR (catalog.iceberg.{view,edit,manage}) para
// que un admin de tenant pueda asignárselo a un usuario puntual vía
// Políticas/Roles (rol dedicado "role_catalog_admin" en
// whisperbi-back-security/functions/security-roles/permissions_catalog.py)
// sin que ese usuario necesite estar en el grupo Cognito "admin" -- mismo
// criterio que las páginas de modelo semántico (semantic.*, también
// permiso granular): el backend autoriza estrictamente, acá alcanza con
// mostrar el error que ya devuelve la API (ver setError/notify arriba) en
// vez de adivinar el acceso del lado del cliente.
const IcebergCatalog = () => <IcebergCatalogContent />;

export default IcebergCatalog;
